import { Fragment, useEffect, useRef } from "react";
import { StyleSheet, TextInput, View } from "react-native";
import { animDuration, colors, invariantColor, radii } from "../utils/design";
import { Space } from "./Space";

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
  },
  input: {
    fontFamily: "geist-mono",
    fontWeight: "400",
    fontSize: 20,
    lineHeight: 20,
    color: invariantColor.white,
    textAlign: "center",
    outlineWidth: 0,

    width: 44,
    height: 52,
    backgroundColor: colors.neutral[800],
    borderWidth: 1,
    borderColor: colors.neutral[700],
    borderRadius: radii[4],
    transitionDuration: animDuration[200],
    transitionProperty: "border-color",
  },
  inputDisabled: {
    backgroundColor: colors.neutral[600],
    color: colors.neutral[200],
  },
  inputInvalid: {
    borderColor: colors.negative[500],
  },
});

export type InputOtpProps = {
  nbDigits: number;
  value: string;
  invalid: boolean;
  autofocus?: boolean;
  disabled?: boolean;
  onChange: (value: string) => void;
  onComplete: () => void;
};

export const InputOtp: React.FC<InputOtpProps> = ({
  nbDigits,
  value,
  invalid,
  autofocus = false,
  disabled = false,
  onChange,
  onComplete,
}) => {
  const ref = useRef<View | null>(null);

  const focusDigit = (index: number) => {
    const container = ref.current as HTMLDivElement | null;
    const inputs = container?.querySelectorAll("input");
    const input = inputs?.[index];
    input?.focus();
  };

  useEffect(() => {
    if (autofocus) {
      focusDigit(0);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <View ref={ref} style={styles.container}>
      {Array.from({ length: nbDigits }).map((_, index) => (
        <Fragment key={index}>
          {index > 0 && <Space width={4} />}
          <TextInput
            style={[styles.input, disabled && styles.inputDisabled, invalid && styles.inputInvalid]}
            value={value[index] ?? ""}
            onChangeText={(text) => {
              // if user deletes a digit
              // we remove the last digit and focus the previous one
              if (text.length === 0) {
                const newValue = value.slice(0, value.length - 1);
                onChange(newValue);
                focusDigit(newValue.length - 1);
                return;
              }

              const newValue = value.slice(0, index) + text + value.slice(index + text.length);
              const v = newValue.slice(0, nbDigits);
              onChange(v);

              const nextIndex = index + text.length;

              // focus next digit
              if (nextIndex < nbDigits) {
                focusDigit(nextIndex);
              } else {
                // if user filled the last digit
                focusDigit(nbDigits - 1);
                onComplete();
              }
            }}
            keyboardType="number-pad"
            textContentType="oneTimeCode"
            enterKeyHint="next"
            selectTextOnFocus={true}
          />
        </Fragment>
      ))}
    </View>
  );
};
